import { supabase } from '@/lib/supabase/client'

export type MemberRole = 'admin' | 'data_entry' | 'approver' | 'viewer'

export interface Member {
  id: string
  userId: string
  name: string
  email: string
  role: MemberRole
  joinedAt: string
  isDisabled: boolean
}

export async function fetchMembers(projectId: string): Promise<Member[]> {
  const { data, error } = await supabase
    .from('project_memberships')
    .select(`
      id, role, joined_at, user_id,
      user:user_profiles!user_id(full_name, is_disabled)
    `)
    .eq('project_id', projectId)
    .eq('is_removed', false)
    .order('joined_at')

  if (error) throw error

  return (data ?? []).map((m) => {
    const u = m.user as { full_name: string; is_disabled: boolean } | null
    return {
      id: m.id,
      userId: m.user_id,
      name: u?.full_name ?? 'ناشناس',
      email: m.user_id,
      role: m.role as MemberRole,
      joinedAt: m.joined_at,
      isDisabled: u?.is_disabled ?? false,
    }
  })
}

export async function fetchUserByEmail(email: string) {
  const { data } = await supabase
    .from('user_profiles')
    .select('id, full_name, is_disabled')
    .ilike('id', email)
    .maybeSingle()
  return data
}

export async function searchUsersByEmail(email: string) {
  // Search auth users by email via user_profiles — we store auth user id as profile id
  // In this setup, we look up by the user's email in auth (best effort via RPC if available)
  // Fallback: admin invites by creating a membership for an existing user_profile
  const { data } = await supabase.auth.admin?.listUsers?.() ?? { data: null }
  if (!data) return []
  return (data.users ?? [])
    .filter((u) => u.email?.toLowerCase().includes(email.toLowerCase()))
    .slice(0, 10)
    .map((u) => ({ id: u.id, email: u.email ?? '', name: u.user_metadata?.full_name ?? u.email ?? '' }))
}

export async function inviteMember(
  projectId: string,
  userId: string,
  role: MemberRole,
  addedBy: string
) {
  // Check if already a member
  const { data: existing } = await supabase
    .from('project_memberships')
    .select('id, is_removed')
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .maybeSingle()

  if (existing && !existing.is_removed) {
    throw new Error('این کاربر قبلاً عضو پروژه است.')
  }

  if (existing?.is_removed) {
    // Re-add
    const { error } = await supabase
      .from('project_memberships')
      .update({ is_removed: false, removed_at: null, role, added_by: addedBy })
      .eq('id', existing.id)
    if (error) throw error
  } else {
    const { error } = await supabase.from('project_memberships').insert({
      project_id: projectId,
      user_id: userId,
      role,
      added_by: addedBy,
    })
    if (error) throw error
  }
}

export async function changeMemberRole(
  membershipId: string,
  newRole: MemberRole
) {
  const { error } = await supabase
    .from('project_memberships')
    .update({ role: newRole })
    .eq('id', membershipId)
  if (error) throw error
}

export async function removeMember(membershipId: string, removedBy: string) {
  const { error } = await supabase
    .from('project_memberships')
    .update({
      is_removed: true,
      removed_at: new Date().toISOString(),
      removed_by: removedBy,
    })
    .eq('id', membershipId)
  if (error) throw error
}
